import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { IDatasource } from 'ag-grid-community';
import { DataGrid, type ColDef, type GridApi } from '../../../../components/ui/table';
import { useAsyncEffect } from '../../../../hooks/useAsyncEffect';
import { fetchMerchantTerminalsGrid, fetchMerchantTerminalsList } from '../../api/api';
import { createMerchantTerminalColumnDefs } from '../../table/columnDefs';
import {
  GRID_PAGE_SIZE,
  GRID_PAGE_SIZE_SELECTOR,
  TERMINALS_GRID_DEFAULT_COL_DEF,
} from '../../table/gridOptions';
import { LIST_CONSOLE_AG_GRID_CLASS } from '../../../../components/shared/ListConsoleLayout';
import type {
  CountryLookup,
  MerchantTerminalGridRow,
  MerchantTerminalRow,
  TerminalsColumnFilters,
  TerminalsGridContext,
} from '../../models/types';
import {
  buildCountryById,
  buildCurrencyById,
  mapAgGridFilterModelToTerminalsFilters,
  resolveCurrencyCodesFromCountryIds,
  resolveDisplayName,
} from '../../utils/utils';
import { ACCENT_COLORS, EMPTY_TERMINALS_COLUMN_FILTERS } from '../../constants/constants';

export type MerchantTerminalsState = {
  rows: MerchantTerminalRow[];
  statsRows: MerchantTerminalRow[];
  loading: boolean;
  error: string | null;
};

type MerchantTerminalsProps = {
  active: boolean;
  debouncedSearch: string;
  columnFilters: TerminalsColumnFilters;
  countryLookups: CountryLookup[];
  countryIdsByCurrency: Record<string, number[]>;
  merchantOptions: Array<{ id: number; name: string }>;
  gridContext: TerminalsGridContext;
  gridApiRef: React.MutableRefObject<GridApi | null>;
  emptyMessage: string;
  onStateChange: (state: MerchantTerminalsState) => void;
  refreshToken?: number;
  gridFilterEpoch?: number;
  skipDatasourceResetRef: React.MutableRefObject<boolean>;
  onGridFilterChanged?: () => void;
};

const MerchantTerminals: React.FC<MerchantTerminalsProps> = ({
  active,
  debouncedSearch,
  columnFilters,
  countryLookups,
  countryIdsByCurrency,
  merchantOptions,
  gridContext,
  gridApiRef,
  emptyMessage,
  onStateChange,
  refreshToken = 0,
  gridFilterEpoch = 0,
  skipDatasourceResetRef,
  onGridFilterChanged,
}) => {
  void gridFilterEpoch;
  const [merchantTerminals, setMerchantTerminals] = useState<MerchantTerminalRow[]>([]);
  const [statsMerchantTerminals, setStatsMerchantTerminals] = useState<MerchantTerminalRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [tablePageSize, setTablePageSize] = useState(GRID_PAGE_SIZE);
  const [error, setError] = useState<string | null>(null);

  const countryById = useMemo(() => buildCountryById(countryLookups), [countryLookups]);
  const currencyById = useMemo(() => buildCurrencyById(countryLookups), [countryLookups]);
  const countryNameById = useMemo(
    () =>
      countryLookups.reduce<Record<number, string>>((acc, country) => {
        acc[country.autoId] = country.countryName;
        return acc;
      }, {}),
    [countryLookups],
  );
  const merchantById = useMemo(
    () =>
      merchantOptions.reduce<Record<number, string>>((acc, merchant) => {
        acc[merchant.id] = merchant.name;
        return acc;
      }, {}),
    [merchantOptions],
  );

  const filtersRef = React.useRef({ debouncedSearch, columnFilters, countryIdsByCurrency });
  filtersRef.current = { debouncedSearch, columnFilters, countryIdsByCurrency };
  const lookupsRef = React.useRef({ countryById, countryNameById, merchantById, currencyById });
  lookupsRef.current = { countryById, countryNameById, merchantById, currencyById };

  const fetchStats = useCallback(async () => {
    try {
      const { rows } = await fetchMerchantTerminalsGrid({
        search: '',
        columnFilters: EMPTY_TERMINALS_COLUMN_FILTERS,
      });
      setStatsMerchantTerminals(rows);
    } catch (e) {
      console.error('Failed to fetch merchant terminal stats:', e);
    }
  }, []);

  const tableDatasource = useMemo<IDatasource>(
    () => ({
      getRows: async (params) => {
        const pageSize = params.endRow - params.startRow;
        const page = pageSize > 0 ? Math.floor(params.startRow / pageSize) : 0;
        setLoading(true);
        setError(null);
        try {
          const rawFilterModel =
            (params.filterModel as Record<string, unknown> | null | undefined) ??
            (params.api?.getFilterModel() as Record<string, unknown> | null | undefined) ??
            null;
          const nativeColumnFilters = mapAgGridFilterModelToTerminalsFilters(rawFilterModel);
          const { rows, totalElements } = await fetchMerchantTerminalsList({
            search: filtersRef.current.debouncedSearch,
            columnFilters: {
              ...filtersRef.current.columnFilters,
              ...nativeColumnFilters,
            },
            countryIdsByCurrency: filtersRef.current.countryIdsByCurrency,
            page,
            size: pageSize,
          });
          const lookups = lookupsRef.current;
          const gridRows: MerchantTerminalGridRow[] = rows.map((t, idx) => {
            const country = t.countryId ? lookups.countryById[t.countryId] : undefined;
            const currencies = resolveCurrencyCodesFromCountryIds(t.currencies, lookups.currencyById);
            return {
              terminalId: t.terminalId,
              name: t.name,
              merchantName: resolveDisplayName(t.merchantName, lookups.merchantById, t.merchantId),
              countryLabel: resolveDisplayName(t.countryName, lookups.countryNameById, t.countryId),
              countryFlag: country?.countryFlag,
              countryId: country?.autoId ?? t.countryId,
              currenciesLabel: currencies.length > 0 ? currencies.join(', ') : '—',
              currencies,
              flowName: t.flowName || '—',
              industryName: t.industryName || '—',
              trafficName: t.trafficName || '—',
              notes: t.notes.trim() || '—',
              successTransactionVolume: t.successTransactionVolume,
              accent: ACCENT_COLORS[idx % ACCENT_COLORS.length],
              raw: t,
              createdAt: t.createdAt,
              updatedAt: t.updatedAt,
            };
          });
          setMerchantTerminals(rows);
          params.successCallback(gridRows, totalElements);
        } catch (e) {
          console.error(e);
          setMerchantTerminals([]);
          setError('Could not load merchant terminals. Please try again.');
          params.failCallback();
        } finally {
          setLoading(false);
        }
      },
    }),
    [],
  );

  useEffect(() => {
    if (!active) return;
    if (skipDatasourceResetRef.current) {
      skipDatasourceResetRef.current = false;
      return;
    }
    gridApiRef.current?.setGridOption('datasource', tableDatasource);
  }, [
    active,
    debouncedSearch,
    columnFilters,
    countryIdsByCurrency,
    countryById,
    countryNameById,
    merchantById,
    currencyById,
    refreshToken,
    tableDatasource,
    gridApiRef,
    skipDatasourceResetRef,
  ]);

  useAsyncEffect(async (isActive) => {
    await fetchStats();
    if (!isActive()) return;
  }, [fetchStats, refreshToken]);

  useEffect(() => {
    onStateChange({
      rows: merchantTerminals,
      statsRows: statsMerchantTerminals,
      loading,
      error,
    });
  }, [merchantTerminals, statsMerchantTerminals, loading, error, onStateChange]);

  const columnDefs = useMemo<ColDef<MerchantTerminalGridRow>[]>(
    () => createMerchantTerminalColumnDefs(),
    [],
  );

  const handleTablePaginationChanged = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) return;
    const newSize = api.paginationGetPageSize();
    if (newSize && newSize !== tablePageSize) {
      setTablePageSize(newSize);
    }
  }, [gridApiRef, tablePageSize]);

  if (!active) return null;

  return (
    <DataGrid<MerchantTerminalGridRow>
      rowModelType="infinite"
      datasource={tableDatasource}
      cacheBlockSize={tablePageSize}
      columnDefs={columnDefs}
      context={gridContext}
      loading={loading}
      pagination
      paginationPageSize={tablePageSize}
      paginationPageSizeSelector={GRID_PAGE_SIZE_SELECTOR}
      height="100%"
      className={LIST_CONSOLE_AG_GRID_CLASS}
      onGridReady={(event) => {
        gridApiRef.current = event.api;
      }}
      defaultColDef={TERMINALS_GRID_DEFAULT_COL_DEF}
      gridOptions={{
        suppressRowClickSelection: true,
        getRowId: (params) => String(params.data?.terminalId ?? ''),
        overlayNoRowsTemplate: `<span class="text-sm text-muted-foreground">${emptyMessage}</span>`,
        onFilterChanged: onGridFilterChanged,
        onPaginationChanged: handleTablePaginationChanged,
      }}
    />
  );
};

export default MerchantTerminals;

export function invalidateMerchantTerminalsCache() {
  // Used by parent via refreshToken increment
}
